import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useEvidenciaDeUnaTarjeta, useImagenDecodificada} from '@/components/bateria/cola/evidencia-de-la-tarjeta'
import type {TarjetaCola} from '@/lib/bateria-cola'

/** Retiene `←`, `→` y el avance tras decidir hasta que el papel del destino está listo, para que no
 * parpadee; las pulsaciones durante la espera se ignoran, no se acumulan. */
export type TransitoCola = {
  /** Hay una navegación retenida: enciende el indicador y apaga DECIDIR. */
  enTransito: boolean
  atras: () => void
  adelante: () => void
  /** El avance de después de decidir o de dejar para después. */
  avanzar: () => void
}

// Pasado este techo, retener se lee como «la cola no responde», que es peor que el parpadeo.
export const TECHO_TRANSITO_MS = 2500

type Gesto = 'atras' | 'adelante' | 'avanzar'

// Sobre `deLaSeccion`, la misma lista del cursor, así el destino se conoce antes de moverse.
const DELTA: Record<Gesto, 1 | -1> = {atras: -1, adelante: 1, avanzar: 1}

export function useTransitoDeLaCola({
  businessId,
  evaluacionId,
  deLaSeccion,
  indice,
  atras,
  adelante,
  avanzar,
}: {
  businessId: string
  evaluacionId: string
  deLaSeccion: TarjetaCola[]
  indice: number
  atras: () => void
  adelante: () => void
  avanzar: () => void
}): TransitoCola {
  const [pendiente, setPendiente] = useState<Gesto | null>(null)
  const [vencido, setVencido] = useState(false)

  // Por ref y no por closure: entre la pulsación y la transición puede llegar un tramo nuevo.
  // Este efecto va antes del que aplica, así que en el mismo ciclo ya se lee la versión fresca.
  const acciones = useRef({atras, adelante, avanzar})
  useEffect(() => {
    acciones.current = {atras, adelante, avanzar}
  })

  const destino = pendiente === null ? undefined : deLaSeccion[indice + DELTA[pendiente]]
  const evidencia = useEvidenciaDeUnaTarjeta(businessId, evaluacionId, destino)
  const decodificada = useImagenDecodificada(evidencia.estado === 'imagen' ? evidencia.url : null)
  // `sin_imagen` es un estado final, no una espera.
  const destinoListo = evidencia.estado === 'sin_imagen' || (evidencia.estado === 'imagen' && decodificada)

  useEffect(() => {
    setVencido(false)
    if (pendiente === null) return
    const id = setTimeout(() => setVencido(true), TECHO_TRANSITO_MS)
    return () => clearTimeout(id)
  }, [pendiente])

  useEffect(() => {
    if (pendiente === null || (!destinoListo && !vencido)) return
    setPendiente(null)
    acciones.current[pendiente]()
  }, [pendiente, destinoListo, vencido])

  const retener = useCallback((gesto: Gesto) => setPendiente((previo) => previo ?? gesto), [])
  const enAtras = useCallback(() => retener('atras'), [retener])
  const enAdelante = useCallback(() => retener('adelante'), [retener])
  const enAvanzar = useCallback(() => retener('avanzar'), [retener])

  // Memoizado: de acá cuelga la identidad del manejador global de teclado, que si no se volvería a
  // suscribir en cada render.
  return useMemo(
    () => ({enTransito: pendiente !== null, atras: enAtras, adelante: enAdelante, avanzar: enAvanzar}),
    [pendiente, enAtras, enAdelante, enAvanzar]
  )
}
